/**
 * @file Poisson-disc sampling (Bridson) with variable density — the placement
 * primitive for trees, rubble, grass tufts and props. Blue-noise beats
 * `rng()*size` for scatter: no clumps, no visible grid.
 */

/** @typedef {import('../types.js').Rng} Rng */

/**
 * Samples points in the rectangle `[0,w] x [0,h]` (LOCAL coordinates — offset
 * them into world space yourself; levels are usually centred on the origin, so
 * the usual call is `p[0] - w/2, p[1] - h/2`).
 *
 * `densityFn(x, z)` returns 0..1:
 *   1   -> points at the base spacing `r`
 *   0.5 -> spacing `r / 0.5` = twice as sparse
 *   0   -> no points at all (masked out: paths, roads, water, clearings)
 *
 * @param {Object} opts
 * @param {number} opts.w Width of the sampling rect.
 * @param {number} opts.h Height (depth) of the sampling rect.
 * @param {number} opts.r Minimum spacing at full density, in the same units.
 * @param {Rng} opts.rng Seeded generator — REQUIRED, no Math.random fallback.
 * @param {(x: number, z: number) => number} [opts.densityFn] Defaults to 1.
 * @param {number} [opts.tries=30] Candidates per active point (Bridson's `k`).
 * @param {number} [opts.maxPoints=100000] Hard safety cap.
 * @param {number} [opts.minDensity=0.08] Densities below this count as 0, which
 *   also bounds the largest local spacing to `r / minDensity`.
 * @returns {Array<[number, number]>} `[[x, z], ...]`
 */
export function poissonDisc2D({
  w,
  h,
  r,
  rng,
  densityFn,
  tries = 30,
  maxPoints = 100000,
  minDensity = 0.08,
}) {
  if (typeof rng !== 'function') throw new Error('poissonDisc2D: a seeded `rng` is required');
  if (!(r > 0) || !(w > 0) || !(h > 0)) return [];

  const density = densityFn || (() => 1);

  // Grid is sized for the SMALLEST possible spacing so lookups stay O(1); with
  // variable radius we must search a neighbourhood scaled by the largest.
  const cell = r / Math.SQRT2;
  const gw = Math.max(1, Math.ceil(w / cell));
  const gh = Math.max(1, Math.ceil(h / cell));
  const grid = new Int32Array(gw * gh).fill(-1);

  const maxSpacing = r / minDensity;
  const searchRadius = Math.ceil(maxSpacing / cell) + 1;

  /** @type {Array<[number, number]>} */
  const points = [];
  /** @type {number[]} Indices into `points`. */
  let active = [];

  /** Local minimum spacing at (x,z); Infinity means "masked out". */
  const spacingAt = (x, z) => {
    const d = density(x, z);
    if (!(d > minDensity)) return Infinity;
    return r / Math.min(1, d);
  };

  const gridIndex = (x, z) => {
    const gx = Math.min(gw - 1, Math.max(0, Math.floor(x / cell)));
    const gz = Math.min(gh - 1, Math.max(0, Math.floor(z / cell)));
    return gz * gw + gx;
  };

  /** Rejects a candidate that is inside anyone's exclusion disc (or its own). */
  const fits = (x, z, spacing) => {
    if (x < 0 || z < 0 || x >= w || z >= h) return false;
    const gx = Math.floor(x / cell);
    const gz = Math.floor(z / cell);
    const x0 = Math.max(0, gx - searchRadius);
    const x1 = Math.min(gw - 1, gx + searchRadius);
    const z0 = Math.max(0, gz - searchRadius);
    const z1 = Math.min(gh - 1, gz + searchRadius);
    for (let cz = z0; cz <= z1; cz++) {
      for (let cx = x0; cx <= x1; cx++) {
        const idx = grid[cz * gw + cx];
        if (idx < 0) continue;
        const p = points[idx];
        const dx = p[0] - x;
        const dz = p[1] - z;
        const d2 = dx * dx + dz * dz;
        // Symmetric test: respect both the candidate's spacing and the
        // neighbour's, so a dense point never lands next to a sparse one.
        const other = spacingAt(p[0], p[1]);
        const need = Math.max(spacing, other === Infinity ? spacing : other);
        if (d2 < need * need) return false;
      }
    }
    return true;
  };

  const push = (x, z) => {
    const idx = points.length;
    points.push([x, z]);
    grid[gridIndex(x, z)] = idx;
    active.push(idx);
    return idx;
  };

  // Seed point: reject up to 64 masked starts before giving up on the region.
  let seeded = false;
  for (let i = 0; i < 64 && !seeded; i++) {
    const x = rng() * w;
    const z = rng() * h;
    if (spacingAt(x, z) !== Infinity) {
      push(x, z);
      seeded = true;
    }
  }
  if (!seeded) return points;

  while (active.length > 0 && points.length < maxPoints) {
    const ai = Math.floor(rng() * active.length);
    const pi = active[ai];
    const [px, pz] = points[pi];
    const spacing = spacingAt(px, pz);
    const base = spacing === Infinity ? r : spacing;

    let placed = false;
    for (let t = 0; t < tries; t++) {
      const ang = rng() * Math.PI * 2;
      // Uniform in the annulus [base, 2*base].
      const rad = base * Math.sqrt(1 + 3 * rng());
      const x = px + Math.cos(ang) * rad;
      const z = pz + Math.sin(ang) * rad;
      const s = spacingAt(x, z);
      if (s === Infinity) continue;
      if (!fits(x, z, s)) continue;
      push(x, z);
      placed = true;
      break;
    }

    if (!placed) {
      // O(1) removal — order of the active list does not affect determinism
      // because the index is drawn from the seeded rng either way.
      active[ai] = active[active.length - 1];
      active.pop();
    }
  }

  return points;
}
